export const getPrimaryActionUrl = (
  id: string,
  type: 'assignment' | 'project' | 'content-research',
  role: UserRole,
  subType?: 'comments' | 'feedback'
): string => {
  switch (role) {
    case 'client':
    case 'writer':
    case 'editor':
      return `/${type}/${id}${subType ? `/${subType}` : ''}`
    case 'writer_ops':
      return `/allotment/writer/${type}/${id}${subType ? `/${subType}` : ''}`
    case 'editor_ops':
      return `/allotment/editor/${type}/${id}${subType ? `/${subType}` : ''}`
    case 'client_ops':
      return `/allotment/client/${type}/${id}${subType ? `/${subType}` : ''}`
    default:
      return ''
  }
}

export const NOTIFICATION_TYPE = (
  id: string,
  role: UserRole
): {
  [key in NotificationType]: {
    id: NotificationType
    title: string
    url: string
    urlTitle: string
    color: string
    className?: string
  }
} => ({
  content_research_report: {
    id: 'content_research_report',
    url: getPrimaryActionUrl(id, 'content-research', role),
    title: 'SEO Tools',
    urlTitle: 'View Report',
    color: 'blue',
    className: 'pill pill-cyan',
  },
  new_assignment_request: {
    id: 'new_assignment_request',
    url: getPrimaryActionUrl(id, 'assignment', role),
    title: 'New Request',
    urlTitle: 'View Assignment',
    color: 'blue',
    className: 'pill pill-cyan',
  },
  assignment_redacted: {
    id: 'assignment_redacted',
    url: getPrimaryActionUrl(id, 'assignment', role),
    title: 'Redacted',
    urlTitle: 'View Assignment',
    color: 'red',
    className: 'pill pill-magenta',
  },
  assignment_rework: {
    id: 'assignment_rework',
    url: getPrimaryActionUrl(id, 'assignment', role),
    title: 'Rework',
    urlTitle: 'View Assignment',
    color: 'volcano',
    className: 'pill pill-magenta',
  },
  assignment_approved: {
    id: 'assignment_approved',
    url: getPrimaryActionUrl(id, 'assignment', role),
    title: 'Approved',
    urlTitle: 'View Assignment',
    color: 'green',
    className: 'pill pill-green',
  },
  assignment_rejected: {
    id: 'assignment_rejected',
    url: getPrimaryActionUrl(id, 'assignment', role),
    title: 'Rejected',
    urlTitle: 'View Assignment',
    color: 'red',
    className: 'pill pill-magenta',
  },
  new_thread_message: {
    id: 'new_thread_message',
    url: getPrimaryActionUrl(id, 'assignment', role, 'comments'),
    title: 'Comment',
    urlTitle: 'View Assignment',
    color: 'blue',
    className: 'pill pill-cyan',
  },
  assignment_feedback: {
    id: 'assignment_feedback',
    url: getPrimaryActionUrl(id, 'assignment', role, 'feedback'),
    title: 'Feedback',
    urlTitle: 'View Feedback',
    color: 'purple',
    className: 'pill pill-cyan',
  },
  assignment_deadline_passed: {
    id: 'assignment_deadline_passed',
    url: getPrimaryActionUrl(id, 'assignment', role),
    title: 'Deadline',
    urlTitle: 'View Assignment',
    color: 'red',
    className: 'pill pill-magenta',
  },
  application_rejected: {
    id: 'application_rejected',
    url: '',
    title: '',
    urlTitle: '',
    color: 'red',
    className: 'pill pill-magenta',
  },
  application_approved: {
    id: 'application_approved',
    url: '',
    title: '',
    urlTitle: '',
    color: 'green',
    className: 'pill pill-green',
  },
  project_created: {
    id: 'project_created',
    url: getPrimaryActionUrl(id, 'project', role),
    title: 'Project Created',
    urlTitle: 'View Project',
    color: 'blue',
    className: 'pill pill-cyan',
  },
  assignment_delivered: {
    id: 'assignment_delivered',
    url: getPrimaryActionUrl(id, 'assignment', role),
    title: 'Assignment Delivered',
    urlTitle: 'View Assignment',
    color: 'cyan',
    className: 'pill pill-yellow',
  },
  project_approval_pending: {
    id: 'project_approval_pending',
    url: getPrimaryActionUrl(id, 'project', role),
    title: 'Review Pending',
    urlTitle: 'View Project',
    color: 'cyan',
    className: 'pill pill-yellow',
  },
  project_completed: {
    id: 'project_completed',
    url: getPrimaryActionUrl(id, 'project', role),
    title: 'Project Completed',
    urlTitle: 'View Project',
    color: 'green',
    className: 'pill pill-green',
  },
  rework_from_client: {
    id: 'rework_from_client',
    url: getPrimaryActionUrl(id, 'assignment', role),
    title: 'Rework',
    urlTitle: 'View Assignment',
    color: 'volcano',
    className: 'pill pill-magenta',
  },
  rejection_from_client: {
    id: 'rejection_from_client',
    url: getPrimaryActionUrl(id, 'assignment', role),
    title: 'Rejection',
    urlTitle: 'View Assignment',
    color: 'red',
    className: 'pill pill-magenta',
  },
  new_project_request: {
    id: 'new_project_request',
    url: getPrimaryActionUrl(id, 'project', role),
    title: 'New Request',
    urlTitle: 'View Project',
    color: 'blue',
    className: 'pill pill-cyan',
  },
  creator_passed_assignment: {
    id: 'creator_passed_assignment',
    url: getPrimaryActionUrl(id, 'assignment', role),
    title: 'Passed',
    urlTitle: 'View Assignment',
    color: 'default',
    className: 'pill pill-cyan',
  },
  assignment_rework_request: {
    id: 'assignment_rework_request',
    url: getPrimaryActionUrl(id, 'assignment', role),
    title: 'Rework',
    urlTitle: 'View Assignment',
    color: 'volcano',
    className: 'pill pill-magenta',
  },
  deadline_crossed: {
    id: 'deadline_crossed',
    url: getPrimaryActionUrl(id, 'assignment', role),
    title: 'Deadline',
    urlTitle: 'View Assignment',
    color: 'red',
    className: 'pill pill-magenta',
  },
  deadline_revised: {
    id: 'deadline_revised',
    url: getPrimaryActionUrl(id, 'assignment', role),
    title: 'Deadline',
    urlTitle: 'View Assignment',
    color: 'blue',
    className: 'pill pill-cyan',
  },
  change_request_approved: {
    id: 'change_request_approved',
    url: '/profile/professional',
    title: 'Request Approved',
    urlTitle: 'View Details',
    color: 'green',
    className: 'pill pill-green',
  },
  change_request_rejected: {
    id: 'change_request_approved',
    url: '/profile/professional',
    title: 'Request Rejected',
    urlTitle: 'View Details',
    color: 'red',
    className: 'pill pill-magenta',
  },
  pay_scale_accepted: {
    id: 'pay_scale_accepted',
    url: '',
    title: 'Pay Scale Accepted',
    urlTitle: '',
    color: 'green',
    className: 'pill pill-green',
  },
  assignee_pay_revised: {
    id: 'assignee_pay_revised',
    url: '',
    title: 'Pay Revision',
    urlTitle: '',
    color: 'green',
  },
  assignment_on_hold: {
    id: 'assignment_on_hold',
    url: getPrimaryActionUrl(id, 'assignment', role),
    title: 'Assignment on Hold',
    urlTitle: 'View Assignment',
    color: 'purple',
    className: 'pill pill-magenta',
  },
  assignment_resolved: {
    id: 'assignment_resolved',
    url: getPrimaryActionUrl(id, 'assignment', role),
    title: 'Assignment Resolved',
    urlTitle: 'View Assignment',
    color: 'green',
    className: 'pill pill-green',
  },
  favourite_creator: {
    id: 'favourite_creator',
    url: '',
    title: 'Favourited by Client',
    urlTitle: '',
    color: 'cyan',
    className: 'pill pill-cyan',
  },
  rework_pending: {
    id: 'rework_pending',
    url: '/assignments?currentPage=0&filters=%7B%22stage%22%3A%5B%22rework%22%2C%22rework_request%22%5D%7D&sort=%7B%22sort_by%22%3A%22allotted_date%22%2C%22sort_order%22%3A%22desc%22%7D',
    title: 'Rework',
    urlTitle: '',
    color: 'magenta',
    className: 'pill pill-magenta',
  },
})
